import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core'
import { Subscription } from 'rxjs'
import { FuseqRaidenService } from '../../fuseq-raiden/fuseqRaiden.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { DialogModule } from '@openng/optimus-ui/dialog'

interface EventTimelineItem {
  id: string
  fingerprint: string
  stableKey: string
  correlationKey: string
  eventGroup: string
  eventName: string
  eventType: string
  status: string
  message: string
  progressPct: number
  parentEventId: string | null
  source: string
  createdAt: string
  startedAt: string
  endedAt: string | null
  durationMs: number
  depth: number
  statusClass: string
  categoryColor: string
  ganttLeftPct: number
  ganttWidthPct: number
  ganttRowClass: string
  laneIndex: number
  laneLabel: string
  parentLabel: string
  childrenCount: number
  assignee: string
  isMilestone: boolean
}

import { OptimUiSharedModule } from '../../../shared/optimus-ui-shared.module';
@Component({
  standalone: true,
  imports: [OptimUiSharedModule, CommonModule, FontAwesomeModule, DialogModule],
  selector: 'fq-event-monitor',
  templateUrl: './event-monitor.component.html',
  styleUrls: ['./event-monitor.component.scss']
})
export class EventMonitorComponent implements OnInit, OnDestroy {
  showDialog = false
  events = signal<EventTimelineItem[]>([])
  timelineTicks = signal<string[]>([])
  private readonly storageKey = 'fuseq-event-monitor-state'
  private readonly subscriptions: Subscription[] = []
  /* Limite di sicurezza: evita crescita illimitata in sessioni lunghe. */
  private readonly maxEvents = 300
  private readonly persistDebounceMs = 400
  private persistTimer: ReturnType<typeof setTimeout> | null = null

  private readonly fuseqRaiden = inject(FuseqRaidenService)

  ngOnInit (): void {
    this.loadPersistedEvents()

    this.subscriptions.push(
      this.fuseqRaiden.onEventLogger().subscribe((event: any) => {
        this.handleIncomingEvent(event)
      })
    )

    this.subscriptions.push(
      this.fuseqRaiden.onEvent('SDWANInstallationEvent').subscribe((event: any) => {
        this.handleIncomingEvent(event)
      })
    )

    this.subscriptions.push(
      this.fuseqRaiden.onEvent('SDWANMigrationEvent').subscribe((event: any) => {
        this.handleIncomingEvent(event)
      })
    )

    this.subscriptions.push(
      this.fuseqRaiden.onEvent('SDWANSpeedtestEvent').subscribe((event: any) => {
        this.handleIncomingEvent(event, 'Speedtest')
      })
    )
  }

  ngOnDestroy (): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
    if (this.persistTimer) {
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }
  }

  trackEvent (index: number, event: EventTimelineItem): string {
    return event.stableKey || event.id || `event-${index}`
  }

  toggleDialog (): void {
    this.showDialog = !this.showDialog
  }

  closeDialog (): void {
    this.showDialog = false
  }

  clearEvents (): void {
    this.events.set([])
    this.timelineTicks.set([])
    this.persistEvents(true)
  }

  private handleIncomingEvent (rawEvent: any, knownType: any = null): void {
    const normalized = this.normalizeEvent(rawEvent, knownType)
    if (!normalized) {
      console.warn('[event-monitor] Ignored incoming event: unable to normalize', rawEvent)
      return
    }

    /* console.log('[event-monitor] Received event', {
      eventName: normalized.eventName,
      eventGroup: normalized.eventGroup,
      status: normalized.status,
      startedAt: normalized.startedAt,
      endedAt: normalized.endedAt,
      parentEventId: normalized.parentEventId,
      stableKey: normalized.stableKey
    }) */

    const existingIndex = this.events().findIndex(item =>
      item.stableKey === normalized.stableKey ||
      item.correlationKey === normalized.correlationKey ||
      item.id === normalized.id ||
      (item.fingerprint && normalized.fingerprint && item.fingerprint === normalized.fingerprint)
    )

    if (existingIndex >= 0) {
      const existing = this.events()[existingIndex]
      const isRestart = this.isEventRestart(existing, normalized)

      if (isRestart) {
        this.events.update(arr => {
          const next = [...arr]
          next[existingIndex] = {
            ...existing,
            ...normalized,
            id: existing.id,
            stableKey: existing.stableKey,
            correlationKey: existing.correlationKey || normalized.correlationKey,
            fingerprint: existing.fingerprint,
            createdAt: this.pickLatestDate(existing.createdAt, normalized.createdAt),
            startedAt: normalized.startedAt,
            endedAt: null,
            durationMs: normalized.durationMs,
            progressPct: normalized.progressPct,
            statusClass: this.getStatusClass(normalized.status)
          }
          return next
        })
      } else {
        this.events.update(arr => {
          const next = [...arr]
          next[existingIndex] = {
            ...existing,
            ...normalized,
            id: existing.id,
            stableKey: existing.stableKey,
            correlationKey: existing.correlationKey || normalized.correlationKey,
            fingerprint: existing.fingerprint,
            createdAt: this.pickLatestDate(existing.createdAt, normalized.createdAt),
            startedAt: existing.startedAt || normalized.startedAt,
            endedAt: this.mergeEndDate(existing.endedAt, normalized.endedAt, normalized.status),
            progressPct: this.mergeProgress(existing.progressPct, normalized.progressPct, normalized.status),
            statusClass: this.getStatusClass(normalized.status)
          }
          return next
        })
      }
    } else {
      this.events.update(arr => [normalized, ...arr])
    }

    this.events.set(this.organizeHierarchy(this.buildGanttMetrics(this.trimEvents(this.events()))))

    this.persistEvents()
  }

  private normalizeEvent (rawEvent: any, knownType: any = null): EventTimelineItem | null {
    let payload = rawEvent?.content ?? rawEvent

    if (!payload || typeof payload !== 'object') {
      return null
    }

    if (payload.content && typeof payload.content === 'object') {
      payload = payload.content
    }

    const eventGroup = payload.eventGroup || payload.workflowName || payload.group || payload.type || 'general'
    let eventName = payload.eventName || payload.name || payload.title || payload.type || 'event'
    if (knownType) {
      eventName = knownType + ' - ' + eventName
    }
    const eventType = payload.eventType || (payload.parentEventId ? 'child' : 'step')
    const status = payload.status || payload.progress || 'started'
    const progressPct = Number(payload.progressPct ?? payload.percent ?? 0)
    const message = payload.message || payload.description || `${eventName}`
    const parentEventId = payload.parentEventId || null
    const source = payload.source || payload.origin || 'raiden'
    const createdAt = payload.createdAt || payload.time || new Date().toISOString()
    const startedAt = payload.startedAt || payload.createdAt || payload.time || new Date().toISOString()
    const normalizedStatus = (status || '').toLowerCase()
    const isCompleted = normalizedStatus.includes('complete') || normalizedStatus === 'success' || normalizedStatus === 'ok'
    const endedAt = payload.endedAt || payload.completedAt || (isCompleted ? createdAt : null)
    const assignee = payload.assignee || payload.user || payload.owner || ''
    const eventId = payload.eventId || `${eventGroup}-${eventName}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const fingerprint = this.buildFingerprint(payload, eventGroup, eventName, eventType, parentEventId)
    const correlationKey = this.buildCorrelationKey(payload, eventGroup, eventName, eventType, parentEventId)
    const stableKey = payload.stableKey || payload.correlationKey || correlationKey || fingerprint || eventId
    const resolvedCorrelationKey = correlationKey || stableKey

    return {
      id: eventId,
      fingerprint,
      stableKey,
      correlationKey: resolvedCorrelationKey,
      eventGroup,
      eventName,
      eventType,
      status,
      message,
      progressPct: Number.isFinite(progressPct) ? progressPct : 0,
      parentEventId,
      source,
      createdAt,
      startedAt,
      endedAt,
      durationMs: 0,
      depth: 0,
      statusClass: this.getStatusClass(status),
      categoryColor: '',
      ganttLeftPct: 0,
      ganttWidthPct: 0,
      ganttRowClass: '',
      laneIndex: 0,
      laneLabel: '',
      parentLabel: '',
      childrenCount: 0,
      assignee,
      isMilestone: false
    }
  }

  getStatusClass (status: string): string {
    const normalizedStatus = (status || '').toLowerCase()

    if (normalizedStatus.includes('complete') || normalizedStatus === 'ok' || normalizedStatus === 'success') {
      return 'bg-fq-success text-white'
    }

    if (normalizedStatus.includes('fail') || normalizedStatus.includes('error') || normalizedStatus === 'ko') {
      return 'bg-fq-danger text-white'
    }

    if (normalizedStatus.includes('progress') || normalizedStatus.includes('running') || normalizedStatus.includes('start')) {
      return 'bg-fq-warning text-black'
    }

    return 'bg-fq-secondary text-white'
  }

  private buildGanttMetrics (items: EventTimelineItem[]): EventTimelineItem[] {
    if (!items.length) {
      this.timelineTicks.set([])
      return []
    }

    const withTimes = items.map(item => {
      const startTime = new Date(item.startedAt || item.createdAt).getTime()
      const rawEndTime = item.endedAt ? new Date(item.endedAt).getTime() : NaN
      const endTime = Number.isFinite(rawEndTime)
        ? rawEndTime
        : startTime + Math.max(item.durationMs, 60000)
      return { item, startTime, endTime }
    })

    const startTimes = withTimes.map(x => x.startTime)
    const endTimes = withTimes.map(x => x.endTime)
    const minStart = Math.min(...startTimes)
    const maxEnd = Math.max(...endTimes)
    const range = Math.max(maxEnd - minStart, 1)

    const paddedMin = minStart - range * 0.04
    const paddedMax = maxEnd + range * 0.04
    const paddedRange = Math.max(paddedMax - paddedMin, 1)

    const tickCount = 6
    this.timelineTicks.set(Array.from({ length: tickCount }, (_, index) => {
      const tickTime = paddedMin + (paddedRange * index) / (tickCount - 1)
      return this.formatTimelineDate(new Date(tickTime))
    }))

    return withTimes.map(({ item, startTime, endTime }) => {
      const leftPct = ((startTime - paddedMin) / paddedRange) * 100
      const rightPct = ((endTime - paddedMin) / paddedRange) * 100
      const widthPct = Math.max(1.5, rightPct - leftPct)
      const isMilestone = item.endedAt ? (endTime - startTime < 2000) : (item.progressPct >= 100)

      return {
        ...item,
        ganttLeftPct: Math.max(0, Math.min(100, leftPct)),
        ganttWidthPct: Math.max(0, Math.min(100, widthPct)),
        ganttRowClass: this.getGanttRowClass(item),
        categoryColor: this.getCategoryColor(item.eventGroup),
        isMilestone,
        laneIndex: item.eventType === 'child' ? 1 : 0,
        laneLabel: item.eventType === 'child' ? 'child' : 'parent',
        parentLabel: item.parentEventId ? `parent ${item.parentEventId}` : '',
        childrenCount: 0
      }
    })
  }

  private organizeHierarchy (items: EventTimelineItem[]): EventTimelineItem[] {
    if (!items.length) {
      return []
    }

    const map = new Map<string, EventTimelineItem>()
    items.forEach(item => map.set(item.stableKey, item))

    const childrenMap = new Map<string, EventTimelineItem[]>()
    const roots: EventTimelineItem[] = []

    for (const item of items) {
      const parentId = item.parentEventId
      const isSelfParent = parentId && (parentId === item.id || parentId === item.stableKey)
      const parent = parentId && !isSelfParent
        ? items.find(existing => existing.id === parentId || existing.stableKey === parentId)
        : null

      if (!parent) {
        roots.push(item)
      } else {
        const siblings = childrenMap.get(parent.stableKey) || []
        siblings.push(item)
        childrenMap.set(parent.stableKey, siblings)
      }
    }

    const byStartTime = (a: EventTimelineItem, b: EventTimelineItem): number => {
      const timeA = new Date(a.startedAt).getTime()
      const timeB = new Date(b.startedAt).getTime()
      return (Number.isFinite(timeA) ? timeA : 0) - (Number.isFinite(timeB) ? timeB : 0)
    }

    roots.sort(byStartTime)

    const result: EventTimelineItem[] = []
    const walk = (item: EventTimelineItem, depth: number) => {
      const children = childrenMap.get(item.stableKey) || []
      const sortedChildren = children.sort(byStartTime)

      result.push({
        ...item,
        depth,
        childrenCount: sortedChildren.length
      })

      for (const child of sortedChildren) {
        walk(child, depth + 1)
      }
    }

    for (const root of roots) {
      walk(root, 0)
    }

    return result
  }

  private readonly categoryPalette = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'
  ]

  private getCategoryColor (group: string): string {
    const normalized = String(group || 'general').toLowerCase()
    let hash = 0

    for (let i = 0; i < normalized.length; i++) {
      hash = normalized.charCodeAt(i) + ((hash << 5) - hash)
    }

    const index = Math.abs(hash) % this.categoryPalette.length
    return this.categoryPalette[index]
  }

  private formatTimelineDate (date: Date): string {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${day}/${month} ${hours}:${minutes}`
  }

  private buildFingerprint (payload: any, eventGroup: string, eventName: string, eventType: string, parentEventId: string | null): string {
    const keyParts = [
      payload.eventId || '',
      eventGroup,
      eventName,
      eventType,
      parentEventId || '',
      payload.workflowId || payload.processId || payload.jobId || payload.correlationId || payload.requestId || payload.transactionId || payload.uuid || ''
    ]

    return keyParts
      .map(part => String(part).trim().toLowerCase())
      .filter(Boolean)
      .join('|')
  }

  private buildCorrelationKey (payload: any, eventGroup: string, eventName: string, eventType: string, parentEventId: string | null): string | null {
    const sources = [
      payload.stableKey,
      payload.correlationKey,
      payload.eventId,
      payload.runId,
      payload.executionId,
      payload.workflowRunId,
      payload.workflowId,
      payload.processId,
      payload.jobId,
      payload.correlationId,
      payload.requestId,
      payload.transactionId,
      payload.uuid,
      parentEventId
    ]

    const stableSource = sources.find(value => value !== undefined && value !== null && String(value).trim() !== '')
    if (stableSource) {
      return String(stableSource).trim().toLowerCase()
    }

    return null
  }

  private pickLatestDate (first: string, second: string): string {
    const firstTime = new Date(first).getTime()
    const secondTime = new Date(second).getTime()

    if (!Number.isFinite(firstTime)) {
      return second
    }

    if (!Number.isFinite(secondTime)) {
      return first
    }

    return secondTime >= firstTime ? second : first
  }

  private mergeProgress (existingProgress: number, incomingProgress: number, status: string): number {
    const normalizedIncoming = Number.isFinite(incomingProgress) ? incomingProgress : 0
    const normalizedExisting = Number.isFinite(existingProgress) ? existingProgress : 0
    const normalizedStatus = (status || '').toLowerCase()

    if (normalizedStatus.includes('complete') || normalizedStatus === 'success' || normalizedStatus === 'ok') {
      return Math.max(normalizedExisting, normalizedIncoming, 100)
    }

    return Math.max(normalizedExisting, normalizedIncoming)
  }

  private mergeEndDate (existingEndedAt: string | null, incomingEndedAt: string | null, status: string): string | null {
    if (existingEndedAt) {
      return existingEndedAt
    }

    if (incomingEndedAt) {
      return incomingEndedAt
    }

    const normalizedStatus = (status || '').toLowerCase()
    if (normalizedStatus.includes('complete') || normalizedStatus === 'success' || normalizedStatus === 'ok') {
      return new Date().toISOString()
    }

    return null
  }

  private isEventRestart (existing: EventTimelineItem, normalized: EventTimelineItem): boolean {
    const incomingStatus = (normalized.status || '').toLowerCase()
    const isStartingStatus = incomingStatus.includes('start') ||
      incomingStatus.includes('running') ||
      incomingStatus.includes('progress')

    if (!isStartingStatus) {
      return false
    }

    if (existing.endedAt) {
      return true
    }

    const existingStart = new Date(existing.startedAt || existing.createdAt).getTime()
    const incomingStart = new Date(normalized.startedAt || normalized.createdAt).getTime()

    if (Number.isFinite(existingStart) && Number.isFinite(incomingStart)) {
      return incomingStart > existingStart
    }

    return false
  }

  buildEventTooltip (item: EventTimelineItem): string {
    const formatDate = (value: string | null): string => {
      if (!value) {
        return '—'
      }
      const date = new Date(value)
      return Number.isFinite(date.getTime()) ? date.toLocaleString('it-IT') : String(value)
    }

    const startTime = new Date(item.startedAt || item.createdAt).getTime()
    const endTime = item.endedAt ? new Date(item.endedAt).getTime() : NaN
    let duration = '—'

    if (Number.isFinite(startTime)) {
      const end = Number.isFinite(endTime) ? endTime : Date.now()
      const totalSeconds = Math.max(0, Math.floor((end - startTime) / 1000))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      duration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return [
      `Task: ${item.eventName}`,
      `Gruppo: ${item.eventGroup}`,
      `Stato: ${item.status}`,
      `Iniziato: ${formatDate(item.startedAt || item.createdAt)}`,
      `Terminato: ${item.endedAt ? formatDate(item.endedAt) : 'in corso'}`,
      `Durata: ${duration}`,
      `Progresso: ${item.progressPct}%`,
      `Sorgente: ${item.source}`
    ].join('\n')
  }

  private getGanttRowClass (item: EventTimelineItem): string {
    return item.eventType === 'child' ? 'event-gantt-row--child' : 'event-gantt-row--root'
  }

  /* Mantiene solo gli eventi più recenti per non far crescere la memoria
     e il payload di sessionStorage all'infinito. */
  private trimEvents (items: EventTimelineItem[]): EventTimelineItem[] {
    if (items.length <= this.maxEvents) {
      return items
    }
    return [...items]
      .sort((a, b) => this.toTime(b.createdAt) - this.toTime(a.createdAt))
      .slice(0, this.maxEvents)
  }

  private toTime (value: string): number {
    const time = new Date(value).getTime()
    return Number.isFinite(time) ? time : 0
  }

  persistEvents (immediate = false): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }

    if (immediate) {
      this.writePersistedEvents()
      return
    }

    this.persistTimer = setTimeout(() => {
      this.persistTimer = null
      this.writePersistedEvents()
    }, this.persistDebounceMs)
  }

  private writePersistedEvents (): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.events()))
    } catch (error) {
      console.warn('Unable to persist event monitor state', error)
    }
  }

  loadPersistedEvents (): void {
    try {
      const raw = sessionStorage.getItem(this.storageKey)
      if (!raw) {
        return
      }
      const persisted = JSON.parse(raw)
      if (Array.isArray(persisted)) {
        this.events.set(persisted.map((item: EventTimelineItem) => ({
          ...item,
          depth: item.depth || 0,
          statusClass: item.statusClass || this.getStatusClass(item.status),
          fingerprint: item.fingerprint || item.id,
          stableKey: item.stableKey || item.fingerprint || item.id,
          correlationKey: item.correlationKey || item.stableKey || item.fingerprint || item.id,
          startedAt: item.startedAt || item.createdAt,
          endedAt: item.endedAt || null,
          durationMs: Number.isFinite(item.durationMs) ? item.durationMs : 0,
          ganttLeftPct: Number.isFinite(item.ganttLeftPct) ? item.ganttLeftPct : 0,
          ganttWidthPct: Number.isFinite(item.ganttWidthPct) ? item.ganttWidthPct : 0,
          ganttRowClass: item.ganttRowClass || this.getGanttRowClass(item),
          categoryColor: item.categoryColor || this.getCategoryColor(item.eventGroup),
          assignee: item.assignee || '',
          isMilestone: !!item.isMilestone
        })))

        this.events.set(this.organizeHierarchy(this.buildGanttMetrics(this.trimEvents(this.events()))))
      }
    } catch (error) {
      console.warn('Unable to load persisted event monitor state', error)
    }
  }
}
